import axios from '../../utils/axios';

export const CONGRESS_API_KEY = 'b613ae3c58355346f664.67d39a8f8be5aa554c25f50c';
export const CONGRESS_TRADES_PAGE_SIZE = 100;
export type TopBoughtRange = '30d' | '3m' | '6m' | '1y';

export interface CongressTradeRecord {
  assetName: string;
  ticker: string | null;
  transactionType: 'P' | 'S' | string;
  transactionDate: string;
  notificationDate: string;
  digitallySignedDate?: string;
  amount: string;
  filingStatus?: string;
  status?: string;
  transactionPricePerShare?: number;
  filingYear?: number;
  docId?: string;
  prefixName?: string;
  firstName: string;
  lastName: string;
  stateDst?: string;
  uniqueInsiderId: string;
  assetType?: string;
  description?: string;
  profileHeadshotUrl?: string;
  source?: string;
}

export interface CongressTickerPeriodStats {
  accumTransactionWins?: number;
  accumTransactionReturns?: number;
  numTransactions?: number;
  accumTransactionValue?: number;
  accumWeightedTransactionReturns?: number;
  accumDollarReturns?: number;
}

export interface CongressTraderTickerStats {
  ticker: string;
  companyName: string;
  logo?: string;
  stats: Record<string, CongressTickerPeriodStats>;
}

export interface CongressProfileTransaction {
  _id?: string;
  ticker: string;
  assetName?: string;
  transactionType: 'P' | 'S' | string;
  transactionDate: string;
  notificationDate?: string;
  digitallySignedDate?: string;
  amount?: string;
  transactionPricePerShare?: number;
  firstName?: string;
  lastName?: string;
  stateDst?: string;
  uniqueInsiderId: string;
  source?: string;
}

export interface CongressProfileStockInfo {
  ticker: string;
  title: string;
  logo?: string;
}

export interface CongressTraderProfile {
  uniqueInsiderId: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
  currentAdministrationCommittees?: string;
  currentAdministrationSubcommittees?: string;
  previousBidenAdministrationCommittees?: string;
  previousBidenAdministrationSubcommittees?: string;
  stateDst?: string;
  purchaseStats: CongressTraderTickerStats[];
  sellingStats: CongressTraderTickerStats[];
  stocksInfo: CongressProfileStockInfo[];
  transactions: CongressProfileTransaction[];
}

export interface CongressPoliticianOption {
  uniqueInsiderId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  imageUrl?: string;
}

export interface TopBoughtCongressStockTransaction {
  uniqueInsiderId: string;
  firstName?: string;
  lastName?: string;
  stateDst?: string;
  profileHeadshotUrl?: string;
  amount?: string;
  digitallySignedDate?: string;
  transactionDate?: string;
  docId?: string;
  source?: string;
}

export interface TopBoughtCongressStock {
  ticker: string | null;
  assetName: string;
  totalUpperBoundAmount: number;
  transactionCount: number;
  transactions: TopBoughtCongressStockTransaction[];
}

function buildQuery(params: Record<string, string | number | undefined>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  });
  return query.toString();
}

export function formatCongressDate(date: Date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function getDefaultCongressDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 30);

  return {
    from: formatCongressDate(from),
    to: formatCongressDate(to)
  };
}

export async function getCongressTrades(params: {
  page?: number;
  transactiontype?: string;
  ticker?: string;
  name?: string;
  from: string;
  to: string;
}): Promise<CongressTradeRecord[]> {
  const query = buildQuery({
    apiKey: CONGRESS_API_KEY,
    page: params.page || 1,
    transactiontype: params.transactiontype || 'ALL',
    ticker: params.ticker,
    name: params.name,
    from: params.from,
    to: params.to
  });

  const response = await axios.get(`/api/v1/stocks/congress-trades?${query}`);
  return response?.data?.data || [];
}

export async function getTopBoughtCongressStocks(params: {
  ticker?: string;
  name?: string;
  limit?: number;
  range?: TopBoughtRange;
}): Promise<TopBoughtCongressStock[]> {
  const query = buildQuery({
    apiKey: CONGRESS_API_KEY,
    ticker: params.ticker,
    name: params.name,
    limit: params.limit,
    range: params.range
  });

  const response = await axios.get(`/api/v1/stocks/congress-trades/top-bought-stocks?${query}`);
  return response?.data?.data || [];
}

export async function searchCongressPoliticians(name: string): Promise<CongressPoliticianOption[]> {
  const query = buildQuery({
    apiKey: CONGRESS_API_KEY,
    name
  });

  const response = await axios.get(`/api/v1/stocks/congress-trades/search-politicians?${query}`);
  return response?.data?.data || [];
}

export async function getCongressTraderProfile(
  uniqueInsiderId: string
): Promise<CongressTraderProfile> {
  const query = buildQuery({
    apiKey: CONGRESS_API_KEY
  });

  const response = await axios.get(
    `/api/v1/stocks/congress-trades/profile/${encodeURIComponent(uniqueInsiderId)}?${query}`
  );

  if (!response?.data?.success || !response?.data?.data) {
    throw new Error('Failed to fetch congress trader profile');
  }

  return response.data.data;
}

export async function getCongressTraderTransactions(
  uniqueInsiderId: string,
  ticker: string
): Promise<CongressProfileTransaction[]> {
  const query = buildQuery({
    apiKey: CONGRESS_API_KEY,
    ticker
  });

  const response = await axios.get(
    `/api/v1/stocks/congress-trades/profile/${encodeURIComponent(
      uniqueInsiderId
    )}/transactions?${query}`
  );

  return response?.data?.data || [];
}

export async function getCongressTraderLastThirtyTrades(
  uniqueInsiderId: string
): Promise<CongressProfileTransaction[]> {
  const query = buildQuery({
    apiKey: CONGRESS_API_KEY
  });

  const response = await axios.get(
    `/api/v1/stocks/congress-trades/profile/${encodeURIComponent(
      uniqueInsiderId
    )}/last-30-trades?${query}`
  );

  return response?.data?.data || [];
}
